import { sitApi } from './sitApiClient';

/**
 * List plan summaries (no full body — fetch each via getPlan(id) when needed).
 * Returned shape per row: { id, name, tags?, notes?, owner?, preconditions?,
 *                          subfolder?, createdAt, updatedAt, createdBy }.
 */
export async function listPlans() {
  const { data } = await sitApi.get('/plans');
  return data.plans ?? [];
}

export async function getPlan(planId) {
  const { data } = await sitApi.get(`/plans/${encodeURIComponent(planId)}`);
  return data;
}

/**
 * Folder tree for the library sidebar. Shape:
 *   [ { engineer, subfolders: [ { name, count }, ... ] }, ... ]
 * Used by the Save-to-Library folder picker (autocomplete from the current
 * engineer's existing subfolders) and the PlanLibrary sidebar.
 */
export async function listFolders() {
  const { data } = await sitApi.get('/plans/folders');
  return data.folders ?? [];
}

/**
 * Create one plan in the library. Body MUST include a valid v1 TestPlan AND
 * a non-empty `subfolder` string for library organization. Backend
 * Ajv-validates the plan and pattern-validates the subfolder before writing
 * DDB.
 *
 * Returns the persisted row: { id, name, plan, subfolder, tags?, notes?,
 *                              owner?, createdAt, updatedAt, createdBy }.
 */
export async function savePlan(plan, subfolder) {
  const { data } = await sitApi.post('/plans', { plan, subfolder });
  return data;
}

/**
 * Save N plans sequentially into a single subfolder. Returns { saved: [...],
 * errors: [...] } so the caller can render a partial-success summary.
 */
export async function savePlansBatch(plans, subfolder) {
  const saved = [];
  const errors = [];
  for (const plan of plans) {
    try {
      const row = await savePlan(plan, subfolder);
      saved.push(row);
    } catch (err) {
      const body = err?.response?.data;
      errors.push({
        plan,
        message: body?.message || err.message,
        code: body?.code,
        context: body?.context
      });
    }
  }
  return { saved, errors };
}

export async function deletePlan(planId) {
  await sitApi.delete(`/plans/${encodeURIComponent(planId)}`);
}
